import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { getIssues, IssueList } from '../../api/issues';
import { ActivityItem, Automation, Follow, getActivity, getAutomation, getFollows } from '../data/cockpit';
import { envFacts, envKind, envTone, exactTone, usCount, useCockpit } from '../data/CockpitContext';
import { clock, clockSeconds, number, until } from '../data/format';
import { usePoll, useNow } from '../data/usePoll';
import { KIND_LABEL, useKindFilter } from '../data/kindFilter';
import { inFocus } from '../data/focus';
import { Card, CheckRow, EnvChip, Empty, ErrorNotice, Freshness, Mark, OwnerChip } from '../components/ui';
import { v2 } from '../paths';
import { ownerOfGroup } from './issueModel';

const SYSTEM_NAME: Record<ActivityItem['system'], string> = { cb: 'Chargebee', cn: 'Connector', ex: 'Exact', lab: 'Lab', dashboard: 'Dashboard' };

const FollowRow = ({ f }: { f: Follow }) => {
    const line = (a: string) => (a === 'ok' ? 'sl ok' : 'sl');
    const chip = f.state.exact === 'ok' ? 'chip ok' : f.state.exact === 'bad' || f.state.connector === 'bad' ? 'chip bad' : 'chip info';
    const to = f.state.issueKey ? v2.issue(f.customer, f.environment, f.state.issueKey) : v2.env(f.customer, f.environment);

    return (
        <Link to={to} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px', borderBottom: '1px solid #161D26' }}>
            <span className="hrow" style={{ justifyContent: 'space-between' }}>
                <span>
                    <b style={{ fontWeight: 600 }}>{f.objectType}</b> <span className="mono">{f.label}</span>
                    <span className="hrow mut" style={{ gap: 6, marginTop: 3, fontSize: 12 }}>
                        <EnvChip kind={f.environment.toLowerCase().startsWith('prod') ? 'prod' : 'test'} environment={f.environment} />
                        {f.customer}
                    </span>
                </span>
                <span className={chip}>{f.state.text}</span>
            </span>
            <span className="stepper">
                <span className="sp">
                    <Mark tone={f.state.chargebee} />
                    Chargebee
                </span>
                <span className={line(f.state.chargebee)} />
                <span className="sp">
                    <Mark tone={f.state.connector} />
                    Connector
                </span>
                <span className={line(f.state.connector)} />
                <span className="sp">
                    <Mark tone={f.state.exact} />
                    Exact
                </span>
            </span>
        </Link>
    );
};

export const DashboardPage = () => {
    const axios = useAxiosSwagger();
    const { shown: environments, focus, loadedAt, fallback, lab } = useCockpit();
    const now = useNow(1000);
    const [paused, setPaused] = useState(false);
    const [kind, setKind] = useKindFilter();

    const issues = usePoll<IssueList>(
        axios ? (s) => getIssues(axios, { status: 'open', customer: focus?.customer, environment: focus?.environment }, s) : null,
        30000,
        [axios, focus?.customer, focus?.environment],
        `issues/${focus?.customer ?? '*'}/${focus?.environment ?? '*'}`
    );
    const automation = usePoll<Automation>(axios ? (s) => getAutomation(axios, s) : null, 30000, [axios], 'automation');
    const follows = usePoll<Follow[]>(axios ? (s) => getFollows(axios, s) : null, 10000, [axios]);
    const activity = usePoll<ActivityItem[]>(axios && !paused ? (s) => getActivity(axios, undefined, s) : null, 5000, [axios, paused]);

    const shown = environments.filter((e) => kind === 'all' || envKind(e) === kind);
    const totals = useMemo(
        () => ({
            us: shown.reduce((n, e) => n + usCount(e), 0),
            cu: shown.reduce((n, e) => n + e.waitingOnCustomer, 0),
            au: shown.reduce((n, e) => n + e.resolvesItself, 0),
            watching: shown.filter((e) => e.watchStatus === 'watching'),
        }),
        [shown]
    );
    const lowest = [...shown]
        .filter((e) => e.exactRemaining !== null && e.exactRemaining !== undefined)
        .sort((a, b) => (a.exactRemaining ?? 0) - (b.exactRemaining ?? 0))[0];

    const actions = (issues.data?.groups ?? [])
        .filter((g) => usCount(g) + g.waitingOnCustomer > 0)
        .sort((a, b) => usCount(b) - usCount(a) || b.waitingOnCustomer - a.waitingOnCustomer)
        .slice(0, 6);

    const customers = useMemo(() => {
        const by = new Map<string, typeof shown>();
        shown.forEach((e) => by.set(e.customer, [...(by.get(e.customer) ?? []), e]));
        return Array.from(by.entries());
    }, [shown]);

    const counts = (k: string) => environments.filter((e) => k === 'all' || envKind(e) === k).length;

    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">
                        {new Set(environments.map((e) => e.customer)).size} klanten · {environments.length} omgevingen
                    </div>
                    <h1 className="h1">Dashboard</h1>
                </div>
                <div className="seg" role="group" aria-label="Soort omgeving">
                    {(['all', 'prod', 'test', 'lab'] as const).map((k) => (
                        <button key={k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
                            {KIND_LABEL[k]} · {counts(k)}
                        </button>
                    ))}
                </div>
            </div>

            {fallback && <div className="notice">De API heeft nog geen /Cockpit: queues, prognoses en het lab ontbreken hieronder.</div>}

            <div className="tiles">
                <Link className="card tile us" to={`${v2.issues()}?wie=us`}>
                    <span className="l">
                        <OwnerChip owner="us" />
                        actie nodig
                    </span>
                    <span className="v bad">{totals.us}</span>
                    <span className="s">issues waar wij aan zet zijn</span>
                </Link>
                <Link className="card tile cu" to={`${v2.issues()}?wie=cu`}>
                    <span className="l">
                        <OwnerChip owner="cu" />
                        actie nodig
                    </span>
                    <span className="v warn">{totals.cu}</span>
                    <span className="s">wachten op de klant</span>
                </Link>
                <Link className="card tile" to={`${v2.issues()}?wie=au`}>
                    <span className="l">
                        <OwnerChip owner="au" />
                        geen actie
                    </span>
                    <span className="v">{totals.au}</span>
                    <span className="s">lost zichzelf op</span>
                </Link>
                <Link className="card tile" to={lowest ? v2.exact(lowest.customer, lowest.environment) : v2.exact()}>
                    <span className="l">Exact-budget laagst</span>
                    <span className={`v ${exactTone(lowest)}`}>{number(lowest?.exactRemaining)}</span>
                    <span className="s">
                        {lowest ? `${lowest.customer} ${lowest.environment}` : '–'}
                        {lowest?.exactRunsOutAt ? ` · op over ${until(lowest.exactRunsOutAt, now)}` : ''}
                    </span>
                </Link>
                <Link className="card tile" to={totals.watching[0] ? v2.monitor(totals.watching[0].customer, totals.watching[0].environment) : v2.deploy()}>
                    <span className="l">Monitor</span>
                    <span className="v info">{totals.watching.length}</span>
                    <span className="s">{totals.watching.map((w) => `${w.customer} ${w.environment}`).join(', ') || 'geen deploy bewaakt'}</span>
                </Link>
            </div>

            <div className="g2">
                <div className="stack">
                    <Card title="Actie nodig" right={<span className="mut" style={{ fontSize: 12 }}>meeste objecten boven</span>}>
                        <ErrorNotice error={issues.error} />
                        {issues.data && actions.length === 0 && <Empty>Niets waar iemand aan zet is.</Empty>}
                        {actions.map((g) => {
                            const owner = ownerOfGroup(g);
                            return (
                                <Link key={g.code} className="li" to={`${v2.issues()}?code=${encodeURIComponent(g.code)}`}>
                                    <span className={`sev ${owner}`} />
                                    <span>
                                        <span className="t">{g.title}</span>
                                        <span className="m">
                                            <OwnerChip owner={owner} />
                                            <span>{g.environments.slice(0, 3).join(', ')}{g.environments.length > 3 ? ` +${g.environments.length - 3}` : ''}</span>
                                            <span className="mono">{usCount(g) + g.waitingOnCustomer} objecten</span>
                                        </span>
                                    </span>
                                    <span className="btn sm">Bekijken</span>
                                </Link>
                            );
                        })}
                    </Card>

                    <Card title="Omgevingen" right={<Link className="btn sm ghost" to={v2.environments()}>Alle {environments.length}</Link>}>
                        <div className="envgrid">
                            {customers.map(([customer, envs]) => (
                                <React.Fragment key={customer}>
                                    <div className="envname">
                                        <b>{customer}</b>
                                        {envs.some((e) => e.watchStatus === 'watching') && <span className="mut" style={{ fontSize: 12 }}>deploy bewaakt</span>}
                                    </div>
                                    <div className="envcells">
                                        {envs.map((e) => {
                                            const tone = envTone(e);
                                            const cls = tone === 'ok' ? '' : tone;
                                            return (
                                                <Link key={e.environment} className={`et ${cls}`} to={v2.env(e.customer, e.environment)}>
                                                    <EnvChip kind={envKind(e)} environment={e.environment} />
                                                    <span className="mono mut" style={{ fontSize: 11, justifySelf: 'end' }}>{e.build ?? ''}</span>
                                                    <span className="facts-line">
                                                        {envFacts(e).length === 0 ? <span className="mut">Rustig</span> : envFacts(e).map((f) => <span key={f.text} className={`fact-tag ${f.tone}`}>{f.text}</span>)}
                                                    </span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </Card>
                </div>

                <div className="stack">
                    <Card title="Gevolgd" right={<span className="mut" style={{ fontSize: 12 }}>tot het in Exact staat</span>}>
                        {follows.error && <Empty>{follows.error}</Empty>}
                        {follows.data && follows.data.length === 0 && <Empty>Je volgt nog niets. Volg een object vanuit een issue.</Empty>}
                        {(follows.data ?? []).slice(0, 5).map((f) => (
                            <FollowRow key={f.id} f={f} />
                        ))}
                    </Card>

                    <Card title="Automatisch" right={automation.data ? <span className="chip ok">loopt</span> : undefined}>
                        <div className="cb">
                            {automation.error && <span className="mut">{automation.error}</span>}
                            {automation.data && (
                                <>
                                    <div className="eyebrow" style={{ marginBottom: 4 }}>Afgelopen uur</div>
                                    <div className="checks">
                                        <CheckRow tone="ok" label="Scanrondes" value={number(automation.data.lastHour.rounds)} />
                                        <CheckRow tone="ok" label="Issues zelf nagekeken" value={number(automation.data.lastHour.checked)} />
                                        <CheckRow tone="ok" label="Vanzelf opgelost" value={number(automation.data.lastHour.resolved)} />
                                        <CheckRow tone="ok" label="Exact-calls door het dashboard" value={number(automation.data.lastHour.exactCalls)} />
                                    </div>
                                    <div className="eyebrow" style={{ margin: '14px 0 4px' }}>Komt eraan</div>
                                    <div className="checks">
                                        {automation.data.upcoming.length === 0 && <span className="mut">Niets gepland.</span>}
                                        {automation.data.upcoming.slice(0, 6).map((u, i) => (
                                            <CheckRow
                                                key={i}
                                                tone="wait"
                                                label={u.customer ? `${u.text} · ${u.customer} ${u.environment ?? ''}` : u.text}
                                                value={new Date(u.at).getTime() - now < 3600000 ? `over ${until(u.at, now)}` : clock(u.at)}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>

                    <Card
                        title="Live stroom"
                        right={
                            <button className="btn sm ghost" onClick={() => setPaused(!paused)}>
                                {paused ? 'Hervatten' : 'Pauzeren'}
                            </button>
                        }
                    >
                        {activity.error && <Empty>{activity.error}</Empty>}
                        {activity.data && activity.data.length === 0 && <Empty>Nog geen gebeurtenissen.</Empty>}
                        {(activity.data ?? []).filter((a) => !a.customer || !a.environment || inFocus(focus, { customer: a.customer, environment: a.environment })).slice(0, 12).map((a) => {
                            const body = (
                                <>
                                    <span className="mono mut" style={{ fontSize: 12 }}>{clockSeconds(a.at)}</span>
                                    <span className={`systag ${a.system}`}>{SYSTEM_NAME[a.system]}</span>
                                    <span className="w" title={a.text}>
                                        <span className={a.tone}>{a.text}</span>
                                    </span>
                                    <span className="where">
                                        {a.customer}
                                        {a.environment && (
                                            <EnvChip kind={a.environment.toLowerCase().startsWith('prod') ? 'prod' : lab && lab.customer === a.customer && lab.environment === a.environment ? 'lab' : 'test'} environment={a.environment} />
                                        )}
                                    </span>
                                </>
                            );
                            // A line about one object opens that object, the rest its environment; a scan round has
                            // no environment and opens the issues.
                            const to = !a.customer || !a.environment
                                ? v2.issues()
                                : a.relationId
                                  ? v2.object(a.customer, a.environment, a.relationId)
                                  : v2.env(a.customer, a.environment);
                            return (
                                <Link key={a.id} className="ev hov" to={to}>
                                    {body}
                                </Link>
                            );
                        })}
                        <Freshness
                            items={[
                                { label: 'Omgevingen', at: loadedAt, staleAfter: 60 },
                                { label: 'Issues', at: issues.loadedAt, staleAfter: 90 },
                                { label: 'Stroom', at: activity.loadedAt, staleAfter: 20 },
                                { label: 'Gevolgd', at: follows.loadedAt, staleAfter: 40 },
                            ]}
                        />
                    </Card>
                </div>
            </div>
        </>
    );
};
